/**
 * CaptaZap - variaveis oficiais Meta (rotulos locais; Graph recebe {{1}}, {{2}} ...).
 * V5.2: prioriza o agendamento vinculado ao paciente e nunca substitui um
 * nome ausente por nome de arquivo/anexo ou profissional generico da conversa.
 */
export const META_TEMPLATE_VARIABLE_GROUPS = [
  { id: "cliente", label: "Cliente / paciente", icon: "👤", items: [
    { key: "cliente.nome", label: "Nome completo", example: "Maria Silva", hint: "Nome do paciente no CRM" },
    { key: "cliente.primeiro_nome", label: "Primeiro nome", example: "Maria", hint: "Primeiro nome do paciente" },
    { key: "cliente.telefone", label: "Telefone", example: "61999998888", hint: "Celular do contato" },
  ] },
  { id: "agendamento", label: "Agendamento", icon: "📅", items: [
    { key: "agendamento.data", label: "Data da consulta", example: "10/11/2026", hint: "Dia agendado" },
    { key: "agendamento.hora", label: "Horário da consulta", example: "14h", hint: "Hora marcada" },
    { key: "agendamento.data_hora", label: "Data e horário", example: "10/11/2026 às 14h", hint: "Dia e hora juntos" },
    { key: "agendamento.profissional", label: "Profissional / dentista", example: "Dra. Malu", hint: "Dentista vinculado ao agendamento do paciente" },
    { key: "agendamento.procedimento", label: "Procedimento", example: "Avaliação odontológica", hint: "Procedimento agendado" },
  ] },
  { id: "empresa", label: "Empresa / clínica", icon: "🏥", items: [
    { key: "empresa.nome", label: "Nome da empresa", example: "Mundo Sorriso", hint: "Nome fantasia" },
    { key: "empresa.endereco", label: "Endereço", example: "Unidade Itapoã/DF", hint: "Endereço da unidade" },
    { key: "empresa.telefone", label: "Telefone da empresa", example: "(61) 3333-3333", hint: "Contato comercial" },
  ] },
  { id: "atendimento", label: "Atendimento", icon: "💬", items: [
    { key: "atendimento.nome", label: "Nome do atendente", example: "Daiane", hint: "Operador logado" },
    { key: "atendimento.protocolo", label: "Número do chamado", example: "12345", hint: "Identificador do atendimento" },
  ] },
];
export const META_TEMPLATE_VARIABLES = META_TEMPLATE_VARIABLE_GROUPS.flatMap(g => g.items);
export const META_TEMPLATE_VARIABLE_BY_KEY = Object.fromEntries(META_TEMPLATE_VARIABLES.map(v => [v.key, v]));

const first = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || typeof value === "object") continue;
    const text = String(value).trim();
    if (text && !["null", "undefined", "0"].includes(text.toLowerCase())) return text;
  }
  return "";
};

export function nomePessoaValidoMeta(value) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text || text.length > 110 || text.length < 2) return "";
  if (/\.(?:pdf|jpe?g|png|webp|gif|mp4|docx?|xlsx?|zip|rar|webm|ogg)(?:\?|$)/i.test(text)) return "";
  if (/^(?:contato|desconhecido|sem nome|cliente|whatsapp|paciente|anexo|arquivo)$/i.test(text)) return "";
  if (/^[A-F\d]{20,}$/i.test(text) || /\d{4,}/.test(text)) return "";
  if (/[<>@/\\]|https?:|\{\{|\}\}/i.test(text)) return "";
  if (!/^[\p{L}\p{M}][\p{L}\p{M}\s.'-]+$/u.test(text)) return "";
  return text;
}

const normId = v => first(v);
const relatedId = o => normId(first(o?.lead_contexto_id, o?.contexto_lead_id, o?.lead_id, o?.id_lead, o?.leadId, o?.lead?.id, o?.lead?.lead_id));
function appointmentDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime();
  const s = first(value); if (!s) return null;
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(s);
  if (m) {const d = new Date(Number(m[1]),Number(m[2])-1,Number(m[3]),Number(m[4]||0),Number(m[5]||0));return Number.isNaN(d.getTime())?null:d.getTime();}
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(?:às\s+)?(\d{1,2})[h:](\d{2}))?/.exec(s);
  if (br){const d=new Date(+br[3],+br[2]-1,+br[1],+(br[4]||0),+(br[5]||0));return Number.isNaN(d.getTime())?null:d.getTime();}
  return null;
}
const dateRaw = a => first(a?.data_agendamento,a?.dataAgendamento,a?.data_consulta,a?.data,a?.inicio,a?.start,a?.start_at,a?.datahora,a?.data_hora,a?.dataHora);
const hourRaw = a => first(a?.hora_agendamento,a?.horaAgendamento,a?.hora_consulta,a?.hora,a?.horario,a?.time);
const disabled = a => /cancel|exclu|desmarc|faltou|nao.comparec|não.comparec|inativ/i.test(first(a?.status_agendamento,a?.statusAgendamento,a?.status));
function pickDate(a) {return appointmentDate(dateRaw(a));}
function belongs(a,leadId,companyId,embedded) {
  if (!a || typeof a !== "object" || disabled(a)) return false;
  const aCompany = normId(first(a?.empresa_id,a?.empresaId));
  if (aCompany && companyId && aCompany !== companyId) return false;
  const aLead = relatedId(a);
  if (aLead && leadId && aLead !== leadId) return false;
  // A agenda global so pode ser correlacionada por identificador do paciente.
  if (!embedded && (!leadId || aLead !== leadId)) return false;
  return pickDate(a) !== null;
}
/** Seleciona a consulta futura mais proxima do paciente; nunca outra lead. */
export function resolverContextoMetaAgendamento({conversa, agendamentos, leadContextoId, empresaId, agora} = {}) {
  const c=conversa||{};
  const leadId=normId(leadContextoId)||relatedId(c);
  const companyId=normId(empresaId)||normId(c.empresa_id);
  const direct=[c.agendamento,c.ultimo_agendamento,c._ultimo_agendamento,c.lead?.agendamento,c.lead?.ultimo_agendamento,c.lead?._ultimo_agendamento,c.lead_contexto?.agendamento,c.leadContexto?.agendamento];
  const local=[c.agendamentos,c.agendamentosLead,c.lead?.agendamentos,c.lead?.agendamentosLead,c.lead_contexto?.agendamentos,c.leadContexto?.agendamentos];
  const candidates=[...direct,...local.flatMap(v=>Array.isArray(v)?v:[])].filter(a=>belongs(a,leadId,companyId,true));
  if (leadId) candidates.push(...(Array.isArray(agendamentos)?agendamentos:[]).filter(a=>belongs(a,leadId,companyId,false)));
  const unique=[...new Set(candidates)];
  const now=Number.isFinite(Number(agora)) ? Number(agora) : Date.now();
  // Apenas consultas de hoje em diante. Um atendimento antigo nao serve para lembrete.
  const today=new Date(now);today.setHours(0,0,0,0);
  const upcoming=unique.filter(a=>pickDate(a)>=today.getTime()).sort((a,b)=>pickDate(a)-pickDate(b));
  return upcoming[0]||null;
}
function formatDate(value) {
  const ts=appointmentDate(value); if (ts===null) return "";
  const d=new Date(ts);return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}
function formatTime(appointment) {
  const s=hourRaw(appointment)||(/(?:T|\s)(\d{1,2}:\d{2})/.exec(dateRaw(appointment))?.[1]||"");
  const m=/^(\d{1,2})[:h](\d{2})/.exec(s);if(!m)return "";
  const h=+m[1],minute=+m[2];if(h>23||minute>59)return "";
  return `${h}h${minute===0?"":String(minute).padStart(2,"0")}`;
}
function personFromDentist(id, dentistas=[]) {
  const needle=normId(id);if (!needle)return "";
  const entry=(Array.isArray(dentistas)?dentistas:[]).find(d=>[d?.id,d?.id_dentista,d?.dentista_id,d?.id_profissional,d?.profissional_id].some(x=>normId(x)===needle));
  return entry?nomePessoaValidoMeta(first(entry.nome,entry.profissional,entry.nome_profissional,entry.nome_dentista)):"";
}
function dentistFromAppointment(a, dentistas=[]) {
  if (!a) return "";
  const id=first(a.id_dentista,a.dentista_id,a.id_profissional,a.profissional_id,typeof a.dentista==="object"?a.dentista.id:"",typeof a.profissional==="object"?a.profissional.id:"");
  const viaId=personFromDentist(id,dentistas);
  if (viaId) return viaId;
  const pessoa=a.profissional;const dentista=a.dentista;
  return nomePessoaValidoMeta(first(a.profissional_nome,a.nome_profissional,a.dentista_nome,a.nome_dentista,typeof dentista==="object"?dentista.nome:dentista,typeof pessoa==="object"?pessoa.nome:pessoa));
}
/** Ausencia de dados confiaveis => sugestao vazia (nunca inventar dentista). */
export function resolverSugestaoVariavelMeta(key,{conversa,empresaNome,operadorNome,telefone,dentistas,agendamentos,leadContextoId,empresaId,agora}={}) {
  const c=conversa||{};const lead=c.lead_contexto||c.leadContexto||c.lead||{};
  const appointment=resolverContextoMetaAgendamento({conversa:c,agendamentos,leadContextoId,empresaId,agora});
  const nome=[lead.nome_lead,lead.nomeLead,lead.nome,c.nome_oficial,c.nomeOficial,c.nome_contato,c.nomeContato,c.nome,c.nome_whatsapp].map(nomePessoaValidoMeta).find(Boolean)||"";
  const date=appointment?formatDate(dateRaw(appointment)):"";
  const time=appointment?formatTime(appointment):"";
  const data={
    "cliente.nome":nome,
    "cliente.primeiro_nome":nome?nome.split(" ")[0]:"",
    "cliente.telefone":first(telefone,c.telefone,c.numero),
    "agendamento.data":date,
    "agendamento.hora":time,
    "agendamento.data_hora":date&&time?`${date} às ${time}`:"",
    "agendamento.profissional":dentistFromAppointment(appointment,dentistas),
    "agendamento.procedimento":first(appointment?.procedimento_nome,appointment?.nome_procedimento,typeof appointment?.procedimento==="object"?appointment.procedimento.nome:appointment?.procedimento),
    "empresa.nome":first(empresaNome,c.empresa_nome,c.empresaNome),
    "empresa.endereco":first(c.endereco_unidade,c.empresa_endereco),
    "empresa.telefone":first(c.empresa_telefone),
    "atendimento.nome":nomePessoaValidoMeta(operadorNome),
    "atendimento.protocolo":first(c.protocolo,c.protocolo_id,c.chamado_id,c.id_chamado),
  };
  return data[key]||"";
}
